import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OctagonX } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useSystemHalt, useSystemHealth, useKillSwitchStatus } from '@/api/hooks/use-system';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { MIN_REASON_LENGTH, MAX_REASON_LENGTH } from '@/shared/lib/constants';
import { cn } from '@/shared/lib/utils';

/**
 * Compact Emergency Halt button for the app header.
 * RBAC-gated: visible for OPERATOR (view halted badge) and ADMIN (can halt).
 */
export function EmergencyHaltButton() {
  const { t } = useTranslation('common');
  const { canSystemHalt, canStopBot } = usePermissions();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [understood, setUnderstood] = useState(false);
  const { mutate: haltSystem, isPending } = useSystemHalt();
  const { data: health } = useSystemHealth();
  const { data: killSwitch } = useKillSwitchStatus();
  const isKillSwitchActive = killSwitch?.active ?? false;

  if (!canSystemHalt) {
    // Show halted badge for OPERATOR users
    if (health?.system_halted && canStopBot) {
      return (
        <Badge variant="destructive" className="text-xs">
          {t('systemHaltedBadge')}
        </Badge>
      );
    }
    return null;
  }

  const reasonTrimmed = reason.trim();
  const isValid =
    reasonTrimmed.length >= MIN_REASON_LENGTH &&
    reasonTrimmed.length <= MAX_REASON_LENGTH &&
    understood;

  function handleClose() {
    if (isPending) return;
    setOpen(false);
    setReason('');
    setUnderstood(false);
  }

  function handleSubmit() {
    if (!isValid) return;
    haltSystem(reasonTrimmed, {
      onSuccess: () => handleClose(),
    });
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setOpen(true)}
            className={cn(
              'gap-1.5 h-8 px-2.5 relative',
              isKillSwitchActive && 'ring-2 ring-red-400 ring-offset-1 ring-offset-background',
            )}
          >
            <OctagonX className="h-4 w-4" />
            <span className="hidden sm:inline text-xs font-semibold">HALT</span>
            {isKillSwitchActive && (
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-400 animate-pulse" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isKillSwitchActive
            ? 'Kill switch ACTIVE — all trading halted'
            : 'Emergency: stop all bots immediately (Ctrl+Shift+K)'}
        </TooltipContent>
      </Tooltip>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose();
        }}
      >
        <DialogContent className="border-destructive">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <OctagonX className="h-5 w-5" />
              Emergency System Halt
            </DialogTitle>
            <DialogDescription className="text-destructive/80 font-medium">
              This will immediately halt ALL running bots across the entire system. All active
              trading will stop.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="header-halt-reason">
                Reason{' '}
                <span className="text-muted-foreground text-xs">
                  ({reasonTrimmed.length}/{MAX_REASON_LENGTH})
                </span>
              </Label>
              <Textarea
                id="header-halt-reason"
                placeholder="Describe the emergency..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={MAX_REASON_LENGTH}
                className="border-destructive/50 focus-visible:ring-destructive"
              />
            </div>

            <div className="flex items-start gap-3 p-3 rounded-md bg-destructive/10 border border-destructive/30">
              <Checkbox
                id="header-halt-understood"
                checked={understood}
                onCheckedChange={(v) => setUnderstood(Boolean(v))}
                className="mt-0.5 border-destructive data-[state=checked]:bg-destructive"
              />
              <Label
                htmlFor="header-halt-understood"
                className="text-sm leading-relaxed cursor-pointer"
              >
                I understand this will halt <strong>ALL bots</strong> immediately and stop all
                trading across the entire system.
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSubmit} disabled={!isValid || isPending}>
              {isPending ? 'Halting System...' : 'Confirm System Halt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
